import React, { useMemo, useState } from 'react';
import {
  AvatarStack,
  COMMON_APP_TYPES,
  DeskEmpty,
  DeskLink,
  Eyebrow,
  NeedsFlag,
  OutlineTag,
  PLAN_SHORT,
  ROW_COLUMNS,
  SectionHead,
  STAGE_LEGEND,
  StageBar,
  TYPE_LABELS,
  TYPE_FULL_LABELS,
  dateLong,
  daysUntil,
  essayTitle,
  isNotStarted,
  lastEdited,
  openTaskCount,
  peopleOnEssay,
  schoolOf,
  spellNumber,
  waitingLabel,
  wordsLabel,
} from './essayShared';

// The essays hub, rendered the same way for whoever is looking at it. The
// student gets the "+ New essay" action through `headerActions`; a parent or
// advisor gets the identical list, grouped and flagged the same way, read
// through their grant. Everything here is a pure function of the essays and
// who is looking, so there is one hub to keep faithful to the design.

// The filter chips, in the order the hub lists them. `match` is what a chip
// counts and filters by; "All" and "Needs you" sit apart from the type chips.
const TYPE_FILTERS = [
  { key: 'COMMON_APP', label: 'CommonApp', match: (e) => COMMON_APP_TYPES.includes(e.type) },
  { key: 'SUPPLEMENTAL', label: 'Supplementals', match: (e) => e.type === 'SUPPLEMENTAL' },
  { key: 'UC_PIQ', label: 'UC PIQ', match: (e) => e.type === 'UC_PIQ' },
  { key: 'COALITION', label: 'Coalition', match: (e) => e.type === 'COALITION' },
  { key: 'SCHOLARSHIP', label: 'Scholarship', match: (e) => e.type === 'SCHOLARSHIP' || e.type === 'OTHER' },
];

const GROUP_MODES = [
  { key: 'school', label: 'school' },
  { key: 'date', label: 'date' },
  { key: 'type', label: 'type' },
];

// Essays a school owns, and so belong under that school's card.
const SCHOOL_GROUPED_TYPES = ['SUPPLEMENTAL', 'COALITION'];

// The families that read as their own list rather than as one school's work.
const FLAT_SECTIONS = [
  { key: 'UC_PIQ', title: 'UC Personal Insight', match: (e) => e.type === 'UC_PIQ' },
  {
    key: 'SCHOLARSHIP',
    title: 'Scholarships & other',
    match: (e) => e.type === 'SCHOLARSHIP' || e.type === 'OTHER',
  },
];

// What comes first in a list of essays: whatever is waiting on somebody, then
// the ones already underway, and the untouched ones last. Ties keep the order
// they were added in, so the list doesn't reshuffle as things are edited.
function sortEssays(essays, self, ownerName) {
  const rank = (essay) => {
    if (waitingLabel(essay, self, ownerName)) return 0;
    if (isNotStarted(essay)) return 2;
    return 1;
  };
  return [...essays].sort(
    (a, b) => rank(a) - rank(b) || new Date(a.createdAt) - new Date(b.createdAt)
  );
}

function Chip({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 13px',
        border: active ? '1px solid var(--college-ink)' : '1px solid var(--college-border)',
        background: active ? 'var(--college-ink)' : 'var(--college-panel)',
        color: active ? 'var(--college-bg)' : 'var(--college-ink-soft)',
        fontFamily: 'inherit',
        fontSize: 12,
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

// One essay row. `serifTitle` is on for the CommonApp table, where the essay's
// own name is the headline, and off inside a school where the school name
// already carries that weight.
function EssayRow({ essay, self, owner, ownerName, onOpen, serifTitle = false, accent = false, last = false, showSchool = false }) {
  const flag = waitingLabel(essay, self, ownerName);
  const open = openTaskCount(essay);
  const people = peopleOnEssay(essay, owner);
  const detail = essay.waitingNote
    ? `${essay.waitingNote} · ${lastEdited(essay.updatedAt)}`
    : isNotStarted(essay)
    ? 'Not started'
    : essay.prompt
    ? essay.prompt.length > 96
      ? `${essay.prompt.slice(0, 96)}…`
      : essay.prompt
    : lastEdited(essay.updatedAt);
  // Outside a school card nothing else names the school, so the row does.
  const school = showSchool ? schoolOf(essay) : null;
  const subtitle = school ? `${school} · ${detail}` : detail;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(essay.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(essay.id);
        }
      }}
      className="desk-essay-row"
      style={{
        display: 'grid',
        gridTemplateColumns: ROW_COLUMNS,
        alignItems: 'center',
        gap: 18,
        padding: serifTitle ? '16px 18px' : '14px 18px',
        borderBottom: last ? 'none' : '1px solid var(--college-border-soft)',
        ...(accent
          ? { borderLeft: `3px solid ${flag ? 'var(--college-ink)' : 'var(--college-border)'}` }
          : null),
        cursor: 'pointer',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          <span
            className={serifTitle ? 'font-serif-editorial' : undefined}
            style={
              serifTitle
                ? { fontSize: 19, color: 'var(--college-ink)' }
                : { fontSize: 14, fontWeight: 500, color: 'var(--college-ink)' }
            }
          >
            {essayTitle(essay)}
          </span>
          {serifTitle && <OutlineTag>{TYPE_LABELS[essay.type]}</OutlineTag>}
          {flag && <NeedsFlag>{flag}</NeedsFlag>}
        </div>
        <p
          style={{
            margin: serifTitle ? '5px 0 0' : '4px 0 0',
            fontSize: 12,
            color: 'var(--college-muted)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {subtitle}
        </p>
      </div>

      <StageBar essay={essay} />

      <span className="font-mono-editorial" style={{ fontSize: 12, color: essay.wordCount != null ? 'var(--college-ink-soft)' : 'var(--college-muted-2)' }}>
        {wordsLabel(essay)}
      </span>

      <span style={{ fontSize: 12, color: open ? 'var(--college-ink-soft)' : 'var(--college-muted-2)' }}>
        {essay.status === 'SUBMITTED' && open === 0
          ? 'Done'
          : open === 0
          ? 'No tasks'
          : `${open} open task${open === 1 ? '' : 's'}`}
      </span>

      {people.length > 1 ? (
        <AvatarStack people={people} />
      ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <AvatarStack people={people} />
        </div>
      )}
    </div>
  );
}

function SchoolCard({ school, essays, self, owner, ownerName, viewerIsOwner, onOpen }) {
  const started = essays.filter((e) => !isNotStarted(e)).length;
  const needing = essays.filter((e) => waitingLabel(e, self, ownerName)).length;
  const app = essays.find((e) => e.collegeApplication)?.collegeApplication || null;

  return (
    <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '13px 18px',
          background: 'var(--college-bg-alt)',
          borderBottom: '1px solid var(--college-border-soft)',
          flexWrap: 'wrap',
        }}
      >
        <span
          className="font-serif-editorial"
          style={{
            width: 24,
            height: 24,
            background: 'var(--college-ink)',
            color: 'var(--college-bg)',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {school.slice(0, 1).toUpperCase()}
        </span>
        <span className="font-serif-editorial" style={{ fontSize: 18, color: 'var(--college-ink)' }}>
          {school}
        </span>
        {app && (
          <span
            style={{
              padding: '3px 10px',
              border: '1px solid var(--college-ink)',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--college-ink)',
            }}
          >
            {PLAN_SHORT[app.plan] || app.plan}
          </span>
        )}
        {app && (
          <span className="font-mono-editorial" style={{ fontSize: 11.5, color: 'var(--college-ink-soft)' }}>
            {dateLong(app.deadline)} · {daysUntil(app.deadline)}d
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--college-muted)' }}>
          {started} of {essays.length} essay{essays.length === 1 ? '' : 's'} started
          {needing > 0 ? ` · ${needing} ${viewerIsOwner ? 'needs you' : 'waiting'}` : ''}
        </span>
      </div>
      {essays.map((essay, i) => (
        <EssayRow
          key={essay.id}
          essay={essay}
          self={self}
          owner={owner}
          ownerName={ownerName}
          onOpen={onOpen}
          last={i === essays.length - 1}
        />
      ))}
    </div>
  );
}

export default function EssaysHubView({
  essays,
  viewer,
  // Whose essays these are. The reader is the owner on the student's own hub,
  // and somebody else's guest on a shared one — the flags and the avatars read
  // from where the reader is standing, so both need saying.
  owner,
  onOpen,
  headerActions = null,
  emptyAction = null,
  eyebrowSuffix = null,
}) {
  const user = viewer;
  const writer = owner || viewer;
  const ownerName = writer?.name;
  const viewerIsOwner = !owner || (owner.kind === viewer?.kind && owner.id === viewer?.id);
  const [filter, setFilter] = useState('ALL');
  const [groupBy, setGroupBy] = useState('school');
  const [showQuiet, setShowQuiet] = useState(false);

  const needsYouCount = useMemo(
    () => essays.filter((e) => waitingLabel(e, user, ownerName)).length,
    [essays, user, ownerName]
  );
  const submittedCount = useMemo(() => essays.filter((e) => e.status === 'SUBMITTED').length, [essays]);

  const visible = useMemo(() => {
    if (filter === 'ALL') return essays;
    if (filter === 'NEEDS_ATTENTION') return essays.filter((e) => waitingLabel(e, user, ownerName));
    const chip = TYPE_FILTERS.find((f) => f.key === filter);
    return chip ? essays.filter(chip.match) : essays;
  }, [essays, filter, user, ownerName]);

  // CommonApp always keeps its own section — it isn't tied to a school.
  const commonApp = useMemo(
    () => sortEssays(visible.filter((e) => COMMON_APP_TYPES.includes(e.type)), user, ownerName),
    [visible, user, ownerName]
  );

  // Supplementals are the ones a school actually owns; the UC and scholarship
  // essays get their own flat sections rather than being filed under a school
  // they don't belong to.
  const bySchoolTypes = useMemo(() => visible.filter((e) => SCHOOL_GROUPED_TYPES.includes(e.type)), [visible]);

  const flatSections = useMemo(
    () =>
      FLAT_SECTIONS.map((section) => ({
        ...section,
        essays: sortEssays(visible.filter(section.match), user, ownerName),
      })).filter((section) => section.essays.length > 0),
    [visible, user, ownerName]
  );

  // Groups of the school-owned essays under the chosen "group by".
  const groups = useMemo(() => {
    if (groupBy === 'type') {
      const byType = new Map();
      bySchoolTypes.forEach((essay) => {
        const key = TYPE_FULL_LABELS[essay.type] || 'Other';
        if (!byType.has(key)) byType.set(key, []);
        byType.get(key).push(essay);
      });
      return Array.from(byType, ([name, items]) => ({ name, essays: sortEssays(items, user, ownerName), deadline: null }));
    }

    const bySchool = new Map();
    bySchoolTypes.forEach((essay) => {
      const key = schoolOf(essay) || 'No school yet';
      if (!bySchool.has(key)) bySchool.set(key, []);
      bySchool.get(key).push(essay);
    });

    const list = Array.from(bySchool, ([name, items]) => ({
      name,
      essays: sortEssays(items, user, ownerName),
      deadline: items.find((e) => e.collegeApplication)?.collegeApplication?.deadline || null,
    }));

    if (groupBy === 'date') {
      list.sort((a, b) => {
        if (!a.deadline) return 1;
        if (!b.deadline) return -1;
        return new Date(a.deadline) - new Date(b.deadline);
      });
    } else {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return list;
  }, [bySchoolTypes, groupBy, user, ownerName]);

  // A school is "quiet" when nothing there is in flight — everything is either
  // untouched or already submitted. Those collapse into one line so the
  // schools actually being worked on stay at eye level.
  const isQuiet = (group) =>
    group.essays.every((e) => isNotStarted(e) || e.status === 'SUBMITTED') &&
    !group.essays.some((e) => waitingLabel(e, user, ownerName));

  const activeGroups = groups.filter((g) => !isQuiet(g));
  const quietGroups = groups.filter(isQuiet);

  const quietSummary = quietGroups
    .map((g) => {
      const n = g.essays.length;
      const allSubmitted = g.essays.every((e) => e.status === 'SUBMITTED');
      const state = allSubmitted ? 'submitted' : n > 1 ? 'both not started' : 'not started';
      return `${g.name} · ${n} essay${n === 1 ? '' : 's'} · ${state}`;
    })
    .join('   ·   ');

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '36px 32px 64px' }}>
      <style>{`
        .desk-essay-row:hover { background: var(--college-bg-alt); }
        @media (max-width: 900px) {
          .desk-essay-row { grid-template-columns: minmax(0,1fr) 110px !important; row-gap: 10px; }
        }
      `}</style>

      {/* ── Page head ─────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
          borderBottom: '2px solid var(--college-ink)',
          paddingBottom: 14,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Eyebrow>
            {essays.length === 0
              ? eyebrowSuffix || 'Nothing written down yet'
              : `${spellNumber(essays.length, { capital: true })} essay${essays.length === 1 ? '' : 's'} · ${spellNumber(
                  submittedCount
                )} submitted${eyebrowSuffix ? ` · ${eyebrowSuffix}` : ''}`}
          </Eyebrow>
          <h1
            className="font-serif-editorial"
            style={{ fontSize: 44, fontWeight: 400, lineHeight: 1, margin: 0, color: 'var(--college-ink)' }}
          >
            Essays
          </h1>
        </div>
        {headerActions && <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>{headerActions}</div>}
      </div>

      {essays.length === 0 ? (
        <div style={{ marginTop: 34 }}>
          <DeskEmpty
            title="No essays yet"
            body="Track your CommonApp personal statement, each school's supplementals, and everything in between — with the people helping you, in one place."
            action={emptyAction}
          />
        </div>
      ) : (
        <>
          {/* ── Filters ─────────────────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '18px 0 0', flexWrap: 'wrap' }}>
            <Chip active={filter === 'ALL'} onClick={() => setFilter('ALL')}>
              All {essays.length}
            </Chip>
            {needsYouCount > 0 && (
              <Chip active={filter === 'NEEDS_ATTENTION'} onClick={() => setFilter('NEEDS_ATTENTION')}>
                {viewerIsOwner ? 'Needs you' : 'Needs someone'} {needsYouCount}
              </Chip>
            )}
            {TYPE_FILTERS.map((chip) => {
              const count = essays.filter(chip.match).length;
              if (count === 0) return null;
              return (
                <Chip key={chip.key} active={filter === chip.key} onClick={() => setFilter(chip.key)}>
                  {chip.label} {count}
                </Chip>
              );
            })}
            <span
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: 'var(--college-muted)',
              }}
            >
              Group by{' '}
              {GROUP_MODES.map((mode, i) => (
                <React.Fragment key={mode.key}>
                  {i > 0 && <span style={{ color: 'var(--college-border)' }}>·</span>}
                  <button
                    type="button"
                    onClick={() => setGroupBy(mode.key)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      cursor: 'pointer',
                      fontWeight: groupBy === mode.key ? 700 : 400,
                      color: groupBy === mode.key ? 'var(--college-ink)' : 'var(--college-muted)',
                      borderBottom: groupBy === mode.key ? '1px solid var(--college-gold)' : '1px solid transparent',
                    }}
                  >
                    {mode.label}
                  </button>
                </React.Fragment>
              ))}
            </span>
          </div>

          {/* ── Stage legend ────────────────────────────────────── */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 26,
              marginTop: 22,
              padding: '12px 16px',
              background: 'var(--college-bg-alt)',
              border: '1px solid var(--college-border-soft)',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                font: "600 10px/1 'Instrument Sans', sans-serif",
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'var(--college-muted-2)',
              }}
            >
              Stage
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, fontSize: 11.5, color: 'var(--college-ink-soft)', flexWrap: 'wrap' }}>
              {STAGE_LEGEND.map((label, i) => (
                <span key={label}>
                  {i + 1} {label}
                </span>
              ))}
            </div>
            <span
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                fontSize: 11.5,
                color: 'var(--college-ink-soft)',
              }}
            >
              <NeedsFlag>{viewerIsOwner ? 'Needs you' : 'Needs someone'}</NeedsFlag>
              <span style={{ color: 'var(--college-muted)' }}>
                flags sit apart from stage — an essay can be mid-polish and still be waiting on someone
              </span>
            </span>
          </div>

          {/* ── CommonApp ───────────────────────────────────────── */}
          {commonApp.length > 0 && (
            <div style={{ marginTop: 34 }}>
              <SectionHead
                title="The CommonApp"
                right={
                  <span className="font-mono-editorial" style={{ fontSize: 11, color: 'var(--college-muted)' }}>
                    {commonApp.length} essay{commonApp.length === 1 ? '' : 's'}
                  </span>
                }
              />
              <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)', borderTop: 'none' }}>
                {commonApp.map((essay, i) => (
                  <EssayRow
                    key={essay.id}
                    essay={essay}
                    self={user}
                    owner={writer}
                    ownerName={ownerName}
                    onOpen={onOpen}
                    serifTitle
                    accent
                    last={i === commonApp.length - 1}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── Everything else, grouped ────────────────────────── */}
          {groups.length > 0 && (
            <div style={{ marginTop: 36 }}>
              <SectionHead
                title={groupBy === 'type' ? 'The rest, by type' : groupBy === 'date' ? 'The rest, by deadline' : 'Supplementals, by school'}
                right={
                  <span className="font-mono-editorial" style={{ fontSize: 11, color: 'var(--college-muted)' }}>
                    {bySchoolTypes.length} essay{bySchoolTypes.length === 1 ? '' : 's'} · {groups.length}{' '}
                    {groupBy === 'type' ? 'types' : `school${groups.length === 1 ? '' : 's'}`}
                  </span>
                }
              />

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
                {activeGroups.map((group) => (
                  <SchoolCard
                    key={group.name}
                    school={group.name}
                    essays={group.essays}
                    self={user}
                    owner={writer}
                    ownerName={ownerName}
                    viewerIsOwner={viewerIsOwner}
                    onOpen={onOpen}
                  />
                ))}

                {quietGroups.length > 0 && !showQuiet && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '13px 18px',
                      background: 'var(--college-bg-alt)',
                      border: '1px dashed var(--college-border)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ fontSize: 12.5, color: 'var(--college-muted)' }}>{quietSummary}</span>
                    <DeskLink style={{ marginLeft: 'auto' }} onClick={() => setShowQuiet(true)}>
                      Show all {groupBy === 'type' ? 'types' : 'schools'} ↓
                    </DeskLink>
                  </div>
                )}

                {quietGroups.length > 0 &&
                  showQuiet &&
                  quietGroups.map((group) => (
                    <SchoolCard
                      key={group.name}
                      school={group.name}
                      essays={group.essays}
                      self={user}
                      owner={writer}
                      ownerName={ownerName}
                      viewerIsOwner={viewerIsOwner}
                      onOpen={onOpen}
                    />
                  ))}

                {quietGroups.length > 0 && showQuiet && (
                  <DeskLink style={{ alignSelf: 'flex-end' }} onClick={() => setShowQuiet(false)}>
                    Hide the quiet ones ↑
                  </DeskLink>
                )}
              </div>
            </div>
          )}

          {/* ── UC and scholarship essays, each its own list ─────── */}
          {flatSections.map((section) => (
            <div key={section.key} style={{ marginTop: 36 }}>
              <SectionHead
                title={section.title}
                right={
                  <span className="font-mono-editorial" style={{ fontSize: 11, color: 'var(--college-muted)' }}>
                    {section.essays.length} essay{section.essays.length === 1 ? '' : 's'}
                  </span>
                }
              />
              <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)', borderTop: 'none' }}>
                {section.essays.map((essay, i) => (
                  <EssayRow
                    key={essay.id}
                    essay={essay}
                    self={user}
                    owner={writer}
                    ownerName={ownerName}
                    onOpen={onOpen}
                    serifTitle
                    accent
                    showSchool
                    last={i === section.essays.length - 1}
                  />
                ))}
              </div>
            </div>
          ))}

          {visible.length === 0 && (
            <div style={{ marginTop: 34 }}>
              <DeskEmpty title="Nothing matches that filter" body="Try another chip, or clear the filter to see everything." />
            </div>
          )}
        </>
      )}

    </div>
  );
}

